import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { collection, query, onSnapshot, updateDoc, doc, addDoc, serverTimestamp } from 'firebase/firestore'; 
import { db } from '../lib/firebase';

import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge'; 
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter 
} from "../components/ui/dialog";

import { 
  User, LayoutDashboard, Clock, MapPin, Briefcase, 
  CheckCircle2, AlertCircle, Zap, Star, ShieldCheck, 
  MessageSquare, PlayCircle, Loader2, ArrowRight, Lock, 
  FileText, Home, Info, History, LifeBuoy, HelpCircle // Adicionado LifeBuoy e HelpCircle
} from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '../components/ui/Navbar'; 
import planoFundo from "../assets/planodefundo.png"; 

const TechnicianDashboard = () => {
  const navigate = useNavigate();
  const { user, userProfile, loading: authLoading } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  const [showFinishDialog, setShowFinishDialog] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [tokenInput, setTokenInput] = useState('');

  const userRole = useMemo(() => userProfile?.userType?.toLowerCase() || 'client', [userProfile]);
  const isProfessional = userRole === 'technician' || userRole === 'admin';
  const isAdmin = userRole === 'admin';
  const isApproved = userProfile?.technicianStatus === 'approved' || isAdmin;

  const workCity = useMemo(() => {
    return userProfile?.professionalData?.address?.city || userProfile?.addressDetails?.city || "Sua Região";
  }, [userProfile]);

  const sendNotification = async (targetUserId, title, message, type, category, link = "") => {
    try {
      await addDoc(collection(db, 'notifications'), {
        userId: targetUserId,
        title,
        message,
        type, 
        category,
        link,
        read: false,
        createdAt: serverTimestamp()
      });
    } catch (error) {
      console.error("Erro ao enviar notificação:", error);
    }
  };

  const loadData = useCallback(() => {
    if (!user?.uid || !isApproved) return;
    setLoading(true);
    const q = query(collection(db, 'orders'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const allOrders = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      const filtered = allOrders.filter(o => {
        const myAreas = userProfile?.professionalData?.areas || [];
        const isMyArea = myAreas.includes(o.serviceArea);
        const isPublic = o.status === 'open' && !o.technicianId && isMyArea;
        const isMine = o.technicianId === user.uid;
        return isPublic || isMine;
      });
      setOrders(filtered);
      setLoading(false);
    });
    return unsubscribe;
  }, [user, userProfile, isApproved]);

  useEffect(() => {
    let unsub;
    if (!authLoading && user && isProfessional && isApproved) {
      unsub = loadData();
    } else if (!authLoading && !isProfessional) {
      navigate('/dashboard');
    }
    return () => unsub && unsub();
  }, [user, isProfessional, authLoading, isApproved, loadData, navigate]);

  const publicRequests = useMemo(() => orders.filter(o => o.status === 'open' && !o.technicianId), [orders]);
  const activeOrders = useMemo(() => orders.filter(o => o.technicianId === user?.uid && o.status === 'in_progress'), [orders, user]);
  const directRequests = useMemo(() => orders.filter(o => o.technicianId === user?.uid && o.status === 'waiting_approval'), [orders, user]);
  const completedOrders = useMemo(() => orders.filter(o => o.technicianId === user?.uid && o.status === 'completed'), [orders, user]);

  const handleOrderAction = async (order, newStatus) => {
    setActionLoading(order.id);
    try {
      await updateDoc(doc(db, 'orders', order.id), {
        status: newStatus,
        technicianId: user.uid,
        technicianName: userProfile?.professionalData?.name || userProfile?.name,
        updatedAt: new Date().toISOString()
      });

      const techName = userProfile?.professionalData?.name || userProfile?.name;
      
      if (newStatus === 'in_progress') {
        await sendNotification(
          order.clientId,
          "Técnico a Caminho! 🛠️",
          `${techName} aceitou seu pedido de ${order.serviceArea} e já iniciou o atendimento.`,
          "client",
          "order",
          "/history"
        );
      } else if (newStatus === 'cancelled') {
        await sendNotification(
          order.clientId,
          "Pedido Recusado",
          `${techName} não pôde aceitar sua solicitação direta no momento. Tente outro profissional.`,
          "client",
          "order",
          "/dashboard"
        );
      }

      toast.success("Pedido atualizado!");
      setShowDetailsDialog(false);
    } catch (e) {
      toast.error("Erro ao processar.");
    } finally {
      setActionLoading(null);
    }
  };

  const handleFinishOrder = async () => {
    if (!tokenInput.trim()) return toast.error("Digite o código.");
    setActionLoading(selectedOrder.id);
    if (tokenInput.trim().toUpperCase() !== selectedOrder.serviceCode.toUpperCase()) {
      setActionLoading(null);
      return toast.error("Código incorreto!");
    }
    try {
      await updateDoc(doc(db, 'orders', selectedOrder.id), {
        status: 'completed',
        completedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });

      await sendNotification(
        selectedOrder.clientId,
        "Serviço Concluído! ⭐",
        `Seu atendimento de ${selectedOrder.serviceArea} foi finalizado. Por favor, avalie o técnico no seu histórico.`,
        "client",
        "review",
        "/history"
      );

      toast.success("Serviço concluído!");
      setShowFinishDialog(false);
      setTokenInput('');
    } catch (e) { toast.error("Erro ao finalizar."); }
    finally { setActionLoading(null); }
  };

  if (authLoading) return <div className="h-screen flex items-center justify-center bg-white"><Loader2 className="animate-spin text-blue-600" /></div>;

  if (!isApproved && !authLoading) {
    return (
      <div className="relative min-h-screen flex items-center justify-center p-6 text-center text-left">
        <div className="fixed inset-0 -z-10 w-full h-full" style={{ backgroundImage: `url(${planoFundo})`, backgroundSize: 'cover', backgroundPosition: 'center' }}><div className="absolute inset-0 bg-slate-900/60" /></div>
        <Card className="max-w-md w-full rounded-[2.5rem] bg-white/95 p-10 border-none shadow-2xl">
          <Clock className="w-16 h-16 text-orange-600 mx-auto mb-6 animate-pulse" />
          <h2 className="text-2xl font-black uppercase italic mb-4">Perfil em Análise</h2>
          <p className="text-slate-500 font-bold mb-8 italic text-sm uppercase">Sua conta de profissional está sendo revisada. Aguarde a liberação para aceitar chamados.</p>
          <Button onClick={() => navigate('/dashboard')} className="bg-blue-600 w-full h-14 rounded-2xl font-black uppercase italic">MODO CLIENTE</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen font-sans text-slate-900 overflow-x-hidden pb-10 bg-orange-50/30 text-left">
      <div className="fixed inset-0 -z-10 opacity-15" style={{ backgroundImage: `url(${planoFundo})`, backgroundSize: 'cover', backgroundPosition: 'center' }}></div>
      <Navbar /> 

      <main className="relative z-10 max-w-7xl mx-auto px-6 pt-28 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8 space-y-10">
          
          <div className="px-4 text-left">
              <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2 italic">GERENCIAMENTO PROFISSIONAL <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div></span>
              <h1 className="text-3xl font-black text-slate-900 uppercase italic tracking-tighter mt-1">Olá, {userProfile?.professionalData?.name?.split(' ')[0] || userProfile?.name?.split(' ')[0]}</h1>
          </div>

          <Tabs defaultValue="public" className="space-y-10">
            <TabsList className="bg-white/90 backdrop-blur-md p-2 rounded-[2rem] shadow-xl border border-white/50 w-full flex overflow-x-auto no-scrollbar">
              <TabsTrigger value="public" className="flex-1 rounded-[1.6rem] font-black uppercase italic text-[10px] py-4 data-[state=active]:bg-orange-600 data-[state=active]:text-white outline-none">Mural ({publicRequests.length})</TabsTrigger>
              <TabsTrigger value="direct" className="flex-1 rounded-[1.6rem] font-black uppercase italic text-[10px] py-4 data-[state=active]:bg-blue-700 data-[state=active]:text-white outline-none">Solicitações ({directRequests.length})</TabsTrigger>
              <TabsTrigger value="accepted" className="flex-1 rounded-[1.6rem] font-black uppercase italic text-[10px] py-4 data-[state=active]:bg-green-600 data-[state=active]:text-white outline-none">Ativos ({activeOrders.length})</TabsTrigger>
              <TabsTrigger value="completed" className="flex-1 rounded-[1.6rem] font-black uppercase italic text-[10px] py-4 data-[state=active]:bg-slate-900 data-[state=active]:text-white outline-none">Concluídos ({completedOrders.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="public">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {publicRequests.map(order => (
                  <Card key={order.id} onClick={() => { setSelectedOrder(order); setShowDetailsDialog(true); }} className="bg-white rounded-[2.5rem] border-none shadow-xl border-t-8 border-orange-500 p-8 cursor-pointer hover:scale-[1.02] transition-all group text-left">
                    <div className="flex justify-between items-start mb-4">
                        <Badge className="bg-orange-50 text-orange-600 border-none font-black text-[9px] uppercase px-3 py-1 italic">Disponível no Mural</Badge>
                        <Info size={18} className="text-slate-200 group-hover:text-orange-500 transition-colors"/>
                    </div>
                    <h3 className="text-2xl font-black uppercase italic text-slate-800 mb-2 leading-none">{order.serviceArea}</h3>
                    <div className="flex items-center gap-2 text-slate-400 font-black text-[10px] uppercase italic"><MapPin className="w-4 h-4 text-orange-500" /> {order.addressDetails?.neighborhood}</div>
                    <p className="text-[9px] font-black text-blue-600 uppercase italic mt-6">Toque para ver detalhes e aceitar</p>
                  </Card>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="direct">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {directRequests.map(order => (
                  <Card key={order.id} onClick={() => { setSelectedOrder(order); setShowDetailsDialog(true); }} className="bg-white rounded-[2.5rem] border-none shadow-xl border-t-8 border-blue-600 p-8 cursor-pointer hover:scale-[1.02] transition-all group text-left">
                    <div className="flex items-center gap-4 mb-6 bg-blue-50 p-3 rounded-2xl">
                       <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center border border-blue-200 overflow-hidden shrink-0">
                         {order.clientPhoto ? <img src={order.clientPhoto} className="w-full h-full object-cover" /> : <User size={20} className="text-blue-600"/>}
                       </div>
                       <h4 className="text-sm font-black uppercase italic text-blue-700 flex-1">{order.clientName}</h4>
                       <Zap size={18} className="text-blue-400 fill-blue-400"/>
                    </div>
                    <h3 className="text-2xl font-black uppercase italic text-slate-800 mb-2 leading-none">{order.serviceArea}</h3>
                    <p className="text-[9px] font-black text-blue-600 uppercase italic text-left">Pedido direto para você. Clique para abrir.</p>
                  </Card>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="accepted">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
                {activeOrders.map(order => (
                  <Card key={order.id} className="bg-white rounded-[2.5rem] border-none shadow-xl border-t-8 border-green-500 p-8 text-left">
                    <div className="flex justify-between mb-6">
                      <Badge className="bg-green-50 text-green-600 border-none font-black text-[9px] uppercase px-3 py-1 italic">Serviço em Execução</Badge>
                      <PlayCircle className="text-green-500 animate-pulse w-5 h-5" />
                    </div>
                    <div className="cursor-pointer mb-6" onClick={() => { setSelectedOrder(order); setShowDetailsDialog(true); }}>
                        <h3 className="text-2xl font-black uppercase italic text-slate-800 mb-2 leading-none">{order.serviceArea}</h3>
                        <p className="text-[10px] font-black text-slate-400 uppercase italic">Ver dados do chamado <ArrowRight size={10} className="inline ml-1"/></p>
                    </div>
                    <div className="grid grid-cols-5 gap-2">
                        <Button onClick={() => navigate(`/chat/${order.clientId}`)} className="col-span-1 h-16 bg-slate-100 text-slate-600 hover:bg-blue-600 hover:text-white rounded-2xl transition-all shadow-md"><MessageSquare size={20}/></Button>
                        <Button onClick={() => { setSelectedOrder(order); setShowFinishDialog(true); }} className="col-span-4 h-16 bg-green-600 hover:bg-green-700 text-white font-black rounded-2xl uppercase italic text-xs shadow-lg gap-2">
                           <CheckCircle2 size={18} /> FINALIZAR SERVIÇO
                        </Button>
                    </div>
                  </Card>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="completed">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
                {completedOrders.map(order => (
                  <Card key={order.id} onClick={() => { setSelectedOrder(order); setShowDetailsDialog(true); }} className="bg-white/80 rounded-[2.5rem] border-none shadow-lg border-l-8 border-slate-900 p-8 cursor-pointer hover:bg-white transition-all text-left">
                    <div className="flex justify-between items-start mb-4">
                        <Badge className="bg-slate-100 text-slate-600 border-none font-black text-[9px] uppercase px-3 py-1 italic">Concluído</Badge>
                        <History size={18} className="text-slate-300"/>
                    </div>
                    <h3 className="text-xl font-black uppercase italic text-slate-800 mb-4 leading-none">{order.serviceArea}</h3>
                    {order.rating > 0 ? (
                      <div className="bg-orange-50 p-4 rounded-2xl border border-orange-100 text-left">
                        <div className="flex items-center gap-1 mb-1">
                          {[...Array(5)].map((_, i) => (<Star key={i} size={10} className={`${i < order.rating ? 'fill-orange-500 text-orange-500' : 'text-slate-300'}`} />))}
                        </div>
                        <p className="text-[10px] font-bold text-slate-600 italic">"{order.ratingComment || 'Sem comentário'}"</p>
                      </div>
                    ) : <p className="text-[9px] font-black text-slate-400 uppercase italic">Aguardando feedback do cliente</p>}
                  </Card>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>

        <div className="lg:col-span-4 space-y-8 text-left">
          <Card className="rounded-[3rem] bg-slate-900 text-white border-none shadow-2xl p-8 relative overflow-hidden group text-left">
            <div className="absolute top-[-20px] right-[-20px] opacity-10 group-hover:scale-110 transition-transform"><CheckCircle2 size={150} /></div>
            <h4 className="text-[10px] font-black uppercase text-orange-500 tracking-[0.3em] mb-6">Meu Desempenho</h4>
            <div className="space-y-6 relative z-10 text-left">
               <div className="flex items-center gap-4 border-b border-white/10 pb-4">
                  <div className="w-12 h-12 rounded-2xl bg-orange-600 flex items-center justify-center shadow-lg"><Star className="fill-white text-white" size={24}/></div>
                  <div className="text-left">
                    <p className="text-[11px] font-black uppercase italic text-slate-400">Avaliações</p>
                    <p className="text-xl font-black italic text-white mt-1">{(userProfile?.reviewCount || 0) < 5 ? "Novo Parceiro" : `${userProfile?.rating?.toFixed(1)} Média`}</p>
                  </div>
               </div>
               <div className="pt-4 text-left"><p className="text-[9px] font-black text-slate-400 uppercase italic mb-2">Cidade Base:</p><p className="text-sm font-black uppercase italic text-white flex items-center gap-2 leading-none"><MapPin size={14} className="text-orange-500" /> {workCity}</p></div>
            </div>
          </Card>

          <Button onClick={() => navigate('/profile-tech')} className="w-full h-16 bg-white border-2 border-slate-100 text-slate-900 font-black rounded-[2rem] text-xs uppercase italic shadow-xl active:scale-95 transition-all">EDITAR PERFIL PROFISSIONAL</Button>

          {/* CARD DE SUPORTE - MESMO VISUAL DO CLIENTDASHBOARD */}
          <div className="bg-blue-600 p-8 rounded-[3rem] shadow-2xl border-t-4 border-blue-400 flex flex-col items-center text-center group hover:bg-blue-700 transition-all text-left">
              <LifeBuoy className="w-12 h-12 text-white mb-4 animate-spin-slow" />
              <h3 className="text-lg font-black text-white uppercase italic tracking-tighter mb-1 leading-tight">PRECISE DE AJUDA?</h3>
              <p className="text-[10px] font-bold text-blue-100 uppercase mb-6 italic opacity-80">Solicite suporte aqui!</p>
              <Button 
                onClick={() => navigate('/support')} 
                className="w-full h-14 bg-white text-blue-600 hover:bg-slate-100 font-black rounded-2xl shadow-xl flex items-center justify-center gap-3 uppercase italic text-[10px] transition-all"
              >
                ABRIR CHAMADO <HelpCircle size={16}/>
              </Button>
          </div>
        </div>
      </main>

      {/* MODAL DE DETALHES */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="bg-white rounded-[2.5rem] border-none shadow-2xl max-w-lg p-10 text-left">
          <DialogHeader className="text-left">
            <DialogTitle className="text-2xl font-black uppercase italic text-slate-900 tracking-tighter flex items-center gap-3"><FileText className="text-blue-600" /> Detalhes da Ordem</DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="my-6 space-y-6 overflow-y-auto max-h-[65vh] pr-2 custom-scrollbar text-left">
                <div className="bg-slate-50 p-7 rounded-[2rem] border border-slate-100 space-y-4 text-left">
                    <div><Label className="text-[9px] font-black uppercase text-blue-600 italic">O que fazer:</Label><p className="text-slate-700 font-bold italic leading-relaxed mt-1">"{selectedOrder.description}"</p></div>
                    <div className="grid grid-cols-2 gap-4 pt-4 border-t border-slate-200">
                        <div><Label className="text-[9px] font-black uppercase text-slate-400 italic">Local:</Label><p className="text-xs font-black uppercase italic flex items-center gap-1"><Home size={14}/> {selectedOrder.propertyType}</p></div>
                        <div><Label className="text-[9px] font-black uppercase text-slate-400 italic">Urgência:</Label><Badge className="bg-orange-100 text-orange-600 border-none uppercase font-black italic text-[9px]">{selectedOrder.urgency}</Badge></div>
                    </div>
                </div>
                <div className="space-y-3 text-left">
                    <Label className="text-[10px] font-black uppercase text-red-500 italic ml-2">Endereço Completo:</Label>
                    <div className="bg-blue-50/50 p-6 rounded-[2rem] border border-blue-100 flex items-start gap-4 text-left"><MapPin className="text-red-500 shrink-0 mt-1" size={22} /><p className="font-black text-slate-700 uppercase italic text-sm leading-tight text-left">{selectedOrder.address}</p></div>
                </div>
                {(selectedOrder.status === 'waiting_approval' || selectedOrder.status === 'open') ? (
                    <div className="flex gap-3 pt-4"><Button onClick={() => handleOrderAction(selectedOrder, 'in_progress')} className="flex-1 h-16 bg-blue-600 hover:bg-blue-700 text-white font-black rounded-2xl uppercase italic text-xs shadow-xl">ACEITAR AGORA</Button>{selectedOrder.status === 'waiting_approval' && <Button onClick={() => handleOrderAction(selectedOrder, 'cancelled')} variant="ghost" className="h-16 px-6 bg-red-50 text-red-600 font-black rounded-2xl uppercase italic text-[10px]">RECUSAR</Button>}</div>
                ) : <Button onClick={() => setShowDetailsDialog(false)} className="w-full h-14 bg-slate-900 text-white font-black rounded-xl uppercase italic text-[10px]">FECHAR INFORMAÇÕES</Button>}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* MODAL DE FINALIZAÇÃO */}
      <Dialog open={showFinishDialog} onOpenChange={setShowFinishDialog}>
        <DialogContent className="bg-white rounded-[2.5rem] border-none shadow-2xl max-w-md p-10 text-left">
          <DialogHeader className="text-left">
            <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mb-6 shadow-inner text-green-600"><Lock size={32} /></div>
            <DialogTitle className="text-2xl font-black uppercase italic text-slate-900 tracking-tighter leading-none">Concluir Serviço</DialogTitle>
          </DialogHeader>
          <div className="my-8 space-y-2 text-left">
                <Label className="text-[10px] font-black uppercase text-slate-400 ml-2 italic">Token do Cliente</Label>
                <Input placeholder="A1B2C3" className="h-16 rounded-2xl border-2 border-slate-100 bg-slate-50 text-center text-2xl font-black tracking-[0.3em] uppercase focus:border-green-500 transition-all outline-none" value={tokenInput} onChange={(e) => setTokenInput(e.target.value)} maxLength={6} />
          </div>
          <DialogFooter className="flex flex-col gap-3">
            <Button onClick={handleFinishOrder} disabled={actionLoading || tokenInput.length < 6} className="w-full h-16 bg-green-600 hover:bg-green-700 text-white rounded-2xl font-black uppercase italic text-xs shadow-xl">
               {actionLoading ? <Loader2 className="animate-spin" /> : "CONFIRMAR E FINALIZAR"}
            </Button>
            <Button variant="ghost" onClick={() => { setShowFinishDialog(false); setTokenInput(''); }} className="w-full font-black uppercase italic text-slate-400 text-[10px]">Voltar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <style dangerouslySetInnerHTML={{ __html: `
        .custom-scrollbar::-webkit-scrollbar { width: 4px; } 
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.1); border-radius: 10px; } 
        .no-scrollbar::-webkit-scrollbar { display: none; } 
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        .animate-spin-slow { animation: spin 4s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      ` }} />
    </div>
  );
};

export default TechnicianDashboard;